import { ChangeDetectionStrategy, Component, ViewContainerRef, afterNextRender, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs';
import { BrnDialogService } from '@spartan-ng/brain/dialog';
import {
  HLM_APP_SHELL_HEADER_H_CLASS,
  HlmAppShellImports,
  NAVTAB_EXACT_MATCH,
  NAVTAB_PREFIX_MATCH,
  type NavItem,
} from '@lite-workspace/shared/helm-ui/app-shell';
import { HlmToastHostComponent } from '@lite-workspace/shared/helm-ui/toast';
import { IS_MOBILE } from '@lite-workspace/utils';
import { HlmDropdownMenuImports } from '@lite-workspace/shared/helm-ui/dropdown-menu';
import { HlmIconComponent } from '@lite-workspace/shared/helm-ui/icon';
import { UploadQueueStore } from '../features/gallery-manage/domain/+state/upload-queue.store';
import { GalleryHiddenPhotosApiService } from '../features/gallery-hidden-photos/domain/infrastructure/gallery-hidden-photos-api.service';
import { UPLOAD_PROGRESS_SOURCE, UploadProgressBarComponent, type UploadProgressSource } from '@lite-workspace/uploader-core';
import { ShareGalleryDialogComponent, type ShareGalleryContext } from '../ui/dialog/share-gallery-dialog.component';
import { AccountMenuComponent } from './account-menu.component';
import { HlmProductSwitcherImports } from '@lite-workspace/shared/helm-ui/product-switcher';
import { HlmTooltipImports } from '@lite-workspace/shared/helm-ui/tooltip';
import { BetaWelcome } from '../ui/dialog/beta-welcome-dialog.component';

/** Сегменты маршрута, на которых ряд вкладок галереи остаётся раскрытым. */
const SECONDARY_SEGMENTS = ['lists', 'stats', 'hidden-photos'] as const;

/** The gallery queue seen through the platform's upload-bar contract. */
function galleryUploadProgress(): UploadProgressSource {
  const uploads = inject(UploadQueueStore);
  // Files the server accepted — the processing phase counts previews out of these, not out of the
  // files that never made it onto the wire.
  const previewsTotal = computed(() => uploads.items().filter(item => item.status !== 'failed').length);
  const settled = computed(() => uploads.totalCount() > 0 && !uploads.active());

  return {
    snapshot: computed(() => ({
      bytesSent: uploads.bytesSent(),
      bytesTotal: uploads.bytesTotal(),
      previewsReady: uploads.readyCount(),
      imagesTotal: previewsTotal(),
      serverDone: settled(),
      // Terminal failure, spec § «Загрузка фото» 20: bytes out, nothing in flight, at least one error.
      failed: settled() && uploads.failedCount() > 0,
      // Finish gate, spec 7/29: photos of the batch whose tile the grid has actually drawn.
      painted: uploads.paintedCount(),
    })),
    bytesSent: uploads.bytesSent,
    bytesTotal: uploads.bytesTotal,
    previewsReady: uploads.readyCount,
    previewsTotal,
    // «Нет связи» while the browser reports itself offline (spec 15).
    stalled: computed(() => !uploads.online()),
    failures: computed(() =>
      uploads
        .failedItems()
        .map(item => ({ name: item.name, reason: item.message, retryable: item.retryable, previewUrl: item.previewUrl })),
    ),
    cancel: () => uploads.cancel(),
    retryAll: () => uploads.retryAll(),
  };
}

@Component({
  selector: 'gallery-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // [Q16] the phone strip sits UNDER the header. hlm-app-shell publishes its height on ITS host, and the
    // toast host below stands next to it — so the shell republishes the same class on its own host.
    class: HLM_APP_SHELL_HEADER_H_CLASS,
  },
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    HlmAppShellImports,
    HlmToastHostComponent,
    HlmDropdownMenuImports,
    HlmIconComponent,
    AccountMenuComponent,
    UploadProgressBarComponent,
    HlmProductSwitcherImports,
    HlmTooltipImports,
  ],
  providers: [{ provide: UPLOAD_PROGRESS_SOURCE, useFactory: galleryUploadProgress }],
  template: `
    <hlm-app-shell [tabs]="navItems()">
      <ng-container hlmAppShellLogo>
        <!-- Below sm a page with the back link has no room for the switcher beside burger, back and actions:
             it yields (still on /galleries). display:contents keeps the switcher a direct item of the logo row. -->
        <div [class]="showBack() ? 'contents max-sm:hidden' : 'contents'">
          <hlm-product-switcher currentProductId="gallery" homeLink="/galleries" />
        </div>
        @if (navItems().length) {
          <hlm-app-shell-navigation [menu]="navMenu">
            <hlm-icon src="/assets/icons/menu.svg" [size]="18" />
          </hlm-app-shell-navigation>
        }
        @if (showBack()) {
          <!-- Below sm with no burger the switcher is hidden and nothing sits left of the divider: drop it. -->
          <div
            class="ml-3.5 flex items-center border-l border-line2 pl-3.5"
            [class]="navItems().length ? '' : 'max-sm:ml-0 max-sm:border-l-0 max-sm:pl-0'"
          >
            <!-- Below md the bar has no room for the label: 34px square, chevron only — the size comes from the
                 spec (§3) and has no token. Under 768px the tooltip is inert (IS_MOBILE), so only aria-label carries it. -->
            <a
              routerLink="/galleries"
              hlmTooltip="На главную"
              aria-label="На главную"
              class="flex size-8.5 items-center justify-center rounded-[var(--r)] border border-transparent
                pointer-coarse:relative pointer-coarse:after:absolute pointer-coarse:after:-inset-1.5
                pointer-coarse:after:content-[''] hover:border-line2
                md:h-7.5 md:w-auto md:justify-start md:gap-2 md:px-2.5"
            >
              <hlm-app-shell-back-link label="На главную">
                <hlm-icon src="/assets/icons/chevron-left.svg" [size]="18" tone="acc" />
              </hlm-app-shell-back-link>
            </a>
          </div>
        }
      </ng-container>

      <ng-container hlmAppShellActions>
        <!-- One wrapper, not loose siblings: the shared actions slot spaces its children by gap-3,
             and this bar's CTA / bell / avatar rhythm is gap-2. -->
        <div class="flex items-center gap-2">
          <gallery-account-menu />
        </div>
      </ng-container>

      <ng-template #navMenu>
        <div hlmDropdownMenu class="min-w-[190px]">
          @for (item of navItems(); track item.label) {
            @if (item.action; as action) {
              <button hlmDropdownMenuItem type="button" (click)="action()">{{ item.label }}</button>
            } @else {
              <!-- gap-0: the count carries its own 4px (ml-1), as on the desktop tab -->
              <a
                hlmDropdownMenuItem
                class="gap-0"
                [routerLink]="item.link"
                routerLinkActive="text-acc font-semibold"
                [routerLinkActiveOptions]="item.exact ? exactMatch : prefixMatch"
              >
                <span>{{ item.label }}</span>
                <hlm-app-shell-count [item]="item" />
              </a>
            }
          }
        </div>
      </ng-template>

      <router-outlet />
    </hlm-app-shell>
    <!-- One bar for the whole cabinet: it keeps running across galleries, lists and settings. -->
    <upload-progress-bar variant="shell" />
    <hlm-toast-host [phone]="isMobile()" />
  `,
  styles: [
    `
      :host {
        display: flex;
        min-height: 100dvh;
        flex-direction: column;
        background: var(--bg);
        color: var(--ink);
        /* The upload bar publishes the room it takes; the page gives it back so no content
           ever sits behind it. 0 when no bar is mounted. */
        padding-bottom: var(--upload-bar-h, 0px);
      }
    `,
  ],
})
export class ShellComponent {
  private readonly router = inject(Router);
  protected readonly isMobile = inject(IS_MOBILE);
  private readonly hidden = inject(GalleryHiddenPhotosApiService);
  protected readonly uploads = inject(UploadQueueStore);
  private readonly dialog = inject(BrnDialogService);
  private readonly vcr = inject(ViewContainerRef);

  // The burger menu mirrors the bar's tabs, so it must mirror its match rules too.
  protected readonly exactMatch = NAVTAB_EXACT_MATCH;
  protected readonly prefixMatch = NAVTAB_PREFIX_MATCH;

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  protected readonly galleryId = computed(() => {
    const match = this.url().match(/^\/galleries\/([^/?#]+)/);
    return match ? match[1] : null;
  });

  /**
   * «Скрытые» живёт на серверном списке скрытых фото, а не на сценах открытой страницы:
   * вкладка нужна одинаково и при заходе по прямой ссылке на /lists, /stats, /hidden-photos,
   * и после возврата фото гостям — сервис снимает строку сам (s1-ac3.3).
   */
  protected readonly hiddenPhotoCount = this.hidden.count;

  constructor() {
    // Ряд вкладок принадлежит шапке, а не странице галереи: счётчик скрытых читаем сами,
    // иначе по прямой ссылке на дополнительную вкладку «Скрытые» не появлялась вовсе.
    effect(() => {
      const id = this.galleryId();
      if (id) void this.hidden.load(id);
    });

    const betaWelcome = inject(BetaWelcome);
    afterNextRender(() => betaWelcome.showOnce('gallery', 'assets/illustrations/galleries/glass-gallery.webp'));
  }

  /** Открытый список несёт свою ссылку «Вернуться» — вторая, «На главную», в шапке лишняя. */
  protected readonly onOpenList = computed(() => /\/lists\/[^/?#]+/.test(this.url()));

  /**
   * Путь назад живёт в шапке: со страницы галереи и из настроек аккаунта — у самой страницы
   * настроек своей ссылки «Назад» больше нет.
   */
  protected readonly showBack = computed(() => (!!this.galleryId() && !this.onOpenList()) || this.url().startsWith('/account'));

  /** «Другое» раскрыт, пока фотограф стоит на одной из дополнительных вкладок. */
  private readonly onSecondaryTab = computed(() => SECONDARY_SEGMENTS.some(s => this.url().includes(`/${s}`)));

  protected readonly navItems = computed<NavItem[]>(() => {
    const id = this.galleryId();
    if (id) {
      const main: NavItem[] = [
        // «Фото» is the gallery index — every sibling tab lives under its path, so a prefix match
        // would keep it lit alongside them.
        { label: 'Фото', link: ['/galleries', id], exact: true },
        { label: 'Дизайн', link: ['/galleries', id, 'design'] },
        { label: 'Настройки', link: ['/galleries', id, 'settings'] },
        // Opens the send window, not a page — a tab in look only.
        { label: 'Отправить', action: () => this.openShare() },
      ];
      // Свёрнутый ряд ведёт в «Списки» — первую дополнительную вкладку: переход туда и есть раскрытие.
      if (!this.onSecondaryTab()) return [...main, { label: 'Другое', link: ['/galleries', id, 'lists'] }];

      return [
        ...main,
        { label: 'Списки', link: ['/galleries', id, 'lists'] },
        { label: 'Статистика', link: ['/galleries', id, 'stats'] },
        ...(this.hiddenPhotoCount() > 0
          ? [{ label: 'Скрытые', link: ['/galleries', id, 'hidden-photos'], count: this.hiddenPhotoCount() }]
          : []),
      ];
    }

    // Вне галереи центр шапки пуст: вход в настройки живёт в меню аватара, а корзина — во вкладках списка.
    return [];
  });

  protected openShare(): void {
    const galleryId = this.galleryId();
    if (!galleryId) return;
    this.dialog.open<ShareGalleryContext, void>(
      ShareGalleryDialogComponent,
      this.vcr,
      { galleryId },
      {
        hasBackdrop: true,
        restoreFocus: false,
      },
    );
  }
}
